// Отчёты владельца: лента чеков дня, ABC «что кормит бизнес»,
// «что дала акция». Каждый — своя ручка API, форма ответа — контракт.
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AbcReport, AvgWeeksReport, ByLocationReport, CallsReport, CashOpsReport, ConsumptionReport, DayChecks, DayCloseReport, DepositsReport, FeedbackReport, FoodcostReport, ForecastReport, HeatReport, HoursReport, KitchenSpeed, LocationsList, ModesReport, PairsReport, PromoReport, SalesRegister, StaffSalesReport } from '@dastarhan/contracts';
import { pctLabel, pctToBp, formatMoney, tengeInWords } from '@dastarhan/core';
import { downloadCsv, tg, downloadXls } from './csv';
import { useAskSheet, useNote } from './ui/ask';

const API = '/api/v1';
const UNIT_RU2: Record<string, string> = { PIECE: 'шт', KG: 'кг', L: 'л', PORTION: 'порц' };
const METHOD_RU: Record<string, string> = { CASH: 'нал', CARD: 'карта', KASPI_QR: 'QR' };

const dstr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function ReportsScreen({ token }: { token: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const [ratesFor, setRatesFor] = useState<{
    id: string; name: string; hourlyRate: number; salesPctBp: number; getsService: boolean;
  } | null>(null);
  const [fixOf, setFixOf] = useState<{ orderId: string; number: number } | null>(null);
  const [copyOf, setCopyOf] = useState<string | null>(null);
  const h = { Authorization: `Bearer ${token}` };
  const [view, setView] = useState<'checks' | 'abc' | 'promo' | 'hours' | 'foodcost' | 'staff' | 'cons' | 'cashops' | 'reviews' | 'calls' | 'speed' | 'deposits' | 'voids' | 'open' | 'payroll'>('checks');
  const [date, setDate] = useState(dstr(new Date()));
  const [from, setFrom] = useState(dstr(new Date(Date.now() - 6 * 86400_000)));
  const [to, setTo] = useState(dstr(new Date()));
  const [locationId, setLocationId] = useState('');
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => LocationsList.parse(await (await fetch(`${API}/menu/locations`, { headers: h })).json()),
  });
  const locQ = locationId ? `&locationId=${locationId}` : '';
  const [dayClose, setDayClose] = useState<DayCloseReport | null>(null);
  useEffect(() => {
    if (!dayClose) return;
    const done = () => setDayClose(null);
    window.addEventListener('afterprint', done);
    const t = setTimeout(() => window.print(), 60);
    return () => { window.removeEventListener('afterprint', done); clearTimeout(t); };
  }, [dayClose]);

  const checks = useQuery({
    queryKey: ['r-checks', date, locationId],
    enabled: view === 'checks',
    queryFn: async () => DayChecks.parse(await (await fetch(`${API}/reports/checks?date=${date}${locQ}`, { headers: h })).json()),
  });
  // Снятые блюда: сколько потеряли, из-за чего и кто. Не для наказания —
  // если половина снятий «низкое качество», разговор нужен с кухней
  const voids = useQuery({
    queryKey: ['voids', from, to],
    queryFn: async () => {
      const r = await fetch(`${API}/reports/voids?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return r.json() as Promise<{
        total: number;
        byReason: { count: number; label: string }[];
        byUser: { count: number; name: string }[];
        rows: { id: string; at: string; name: string; reasonLabel: string;
                comment: string | null; byName: string }[];
      }>;
    },
    enabled: view === 'voids',
  });

  // Открытые счета: гости сидят, счёт напечатан, деньги не получены.
  // В выручку они не входят — и это верно, но владелец должен видеть,
  // сколько висит в зале прямо сейчас
  const openBills = useQuery({
    queryKey: ['open-bills'],
    queryFn: async () => {
      const r = await fetch(`${API}/open-orders?locationId=${locationId ?? ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return r.json() as Promise<{
        rows: {
          orderId: string; tableName: string; openedByName: string;
          openedAt: string; awaitingPay: boolean; discount: number;
          items: { qty: number; unitPrice: number }[];
        }[];
      }>;
    },
    enabled: view === 'open',
    refetchInterval: 30_000,
  });

  // Зарплата зала: часы и проценты раздельно. Это два разных вопроса —
  // «был на месте» и «принёс денег», и складывать их в одну цифру
  // значит потерять оба
  const payroll = useQuery({
    queryKey: ['payroll', from, to],
    queryFn: async () => {
      const r = await fetch(`${API}/reports/payroll?from=${from}&to=${to}`, { headers: h });
      return r.json() as Promise<{
        rows: {
          id: string; name: string; hours: number; checks: number;
          sales: number; service: number; avgCheck: number;
          hourlyRate: number; salesPctBp: number; getsService: boolean;
          byHours: number; byPercent: number; byService: number; total: number;
        }[];
        totals?: { byHours: number; byPercent: number; byService: number; total: number; sales: number };
      }>;
    },
    enabled: view === 'payroll',
  });

  const abc = useQuery({
    queryKey: ['r-abc', from, to, locationId],
    enabled: view === 'abc',
    queryFn: async () => AbcReport.parse(await (await fetch(`${API}/reports/abc?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const heat = useQuery({
    queryKey: ['r-heat', locationId],
    enabled: view === 'hours',
    queryFn: async () => HeatReport.parse(await (await fetch(`${API}/reports/heat${locQ ? `?${locQ.slice(1)}` : ''}`, { headers: h })).json()),
  });
  const hours = useQuery({
    queryKey: ['r-hours', from, to, locationId],
    enabled: view === 'hours',
    queryFn: async () => HoursReport.parse(await (await fetch(`${API}/reports/hours?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const foodcost = useQuery({
    queryKey: ['r-foodcost', from, to, locationId],
    enabled: view === 'foodcost',
    queryFn: async () => FoodcostReport.parse(await (await fetch(`${API}/reports/foodcost?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const cons = useQuery({
    queryKey: ['r-cons', from, to, locationId],
    enabled: view === 'cons',
    queryFn: async () => ConsumptionReport.parse(await (await fetch(`${API}/reports/consumption?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const [ratingFilter, setRatingFilter] = useState(0);
  const pairs = useQuery({
    queryKey: ['r-pairs', locationId],
    enabled: view === 'abc',
    queryFn: async () => PairsReport.parse(await (await fetch(`${API}/reports/pairs${locQ ? `?${locQ.slice(1)}` : ''}`, { headers: h })).json()),
  });
  const aw = useQuery({
    queryKey: ['r-avgweeks', locationId],
    enabled: view === 'checks',
    queryFn: async () => AvgWeeksReport.parse(await (await fetch(`${API}/reports/avg-weeks${locQ ? `?${locQ.slice(1)}` : ''}`, { headers: h })).json()),
  });
  const fc = useQuery({
    queryKey: ['r-forecast', locationId],
    enabled: view === 'checks',
    queryFn: async () => ForecastReport.parse(await (await fetch(`${API}/reports/forecast${locQ ? `?${locQ.slice(1)}` : ''}`, { headers: h })).json()),
  });
  const byLoc = useQuery({
    queryKey: ['r-byloc', from, to],
    enabled: view === 'checks' && !locationId && (locations.data?.length ?? 0) > 1,
    queryFn: async () => ByLocationReport.parse(await (await fetch(`${API}/reports/by-location?from=${from}&to=${to}`, { headers: h })).json()),
  });
  const modes = useQuery({
    queryKey: ['r-modes', from, to, locationId],
    enabled: view === 'hours',
    queryFn: async () => ModesReport.parse(await (await fetch(`${API}/reports/modes?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const deps = useQuery({
    queryKey: ['r-deps', from, to, locationId],
    enabled: view === 'deposits',
    queryFn: async () => DepositsReport.parse(await (await fetch(`${API}/reports/deposits?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const speed = useQuery({
    queryKey: ['r-speed', from, to, locationId],
    enabled: view === 'speed',
    queryFn: async () => KitchenSpeed.parse(await (await fetch(`${API}/reports/kitchen-speed?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const callsRep = useQuery({
    queryKey: ['r-calls', from, to, locationId],
    enabled: view === 'calls',
    queryFn: async () => CallsReport.parse(await (await fetch(`${API}/reports/calls?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const reviews = useQuery({
    queryKey: ['r-reviews', from, to, locationId],
    enabled: view === 'reviews',
    queryFn: async () => FeedbackReport.parse(await (await fetch(`${API}/reports/feedback?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const cashops = useQuery({
    queryKey: ['r-cashops', from, to, locationId],
    enabled: view === 'cashops',
    queryFn: async () => CashOpsReport.parse(await (await fetch(`${API}/reports/cash-ops?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const staffSales = useQuery({
    queryKey: ['r-staff', from, to, locationId],
    enabled: view === 'staff',
    queryFn: async () => StaffSalesReport.parse(await (await fetch(`${API}/reports/staff-sales?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });
  const promo = useQuery({
    queryKey: ['r-promo', from, to, locationId],
    enabled: view === 'promo',
    queryFn: async () => PromoReport.parse(await (await fetch(`${API}/reports/promo?from=${from}&to=${to}${locQ}`, { headers: h })).json()),
  });

  return (
    <div className="reports">
      <div className="menu-toolbar">
        <div className="row">
          <button className={`btn ${view === 'checks' ? 'accent' : ''}`} onClick={() => setView('checks')}>Чеки за день</button>
          <button className={`btn ${view === 'abc' ? 'accent' : ''}`} onClick={() => setView('abc')}>ABC</button>
          <button className={`btn ${view === 'voids' ? 'accent' : ''}`} onClick={() => setView('voids')}>Снятия</button>
          <button className={`btn ${view === 'open' ? 'accent' : ''}`} onClick={() => setView('open')}>Открытые счета</button>
          <button className={`btn ${view === 'payroll' ? 'accent' : ''}`} onClick={() => setView('payroll')}>Зарплата</button>
          <button className={`btn ${view === 'hours' ? 'accent' : ''}`} onClick={() => setView('hours')}>По часам</button>
          <button className={`btn ${view === 'foodcost' ? 'accent' : ''}`} onClick={() => setView('foodcost')}>Фудкост</button>
          <button className={`btn ${view === 'staff' ? 'accent' : ''}`} onClick={() => setView('staff')}>Сотрудники</button>
          <button className={`btn ${view === 'cons' ? 'accent' : ''}`} onClick={() => setView('cons')}>Расход</button>
          <button className={`btn ${view === 'cashops' ? 'accent' : ''}`} onClick={() => setView('cashops')}>Внесения</button>
          <button className={`btn ${view === 'reviews' ? 'accent' : ''}`} onClick={() => setView('reviews')}>Отзывы</button>
          <button className={`btn ${view === 'calls' ? 'accent' : ''}`} onClick={() => setView('calls')}>Вызовы</button>
          <button className={`btn ${view === 'speed' ? 'accent' : ''}`} onClick={() => setView('speed')}>Кухня</button>
          <button className={`btn ${view === 'deposits' ? 'accent' : ''}`} onClick={() => setView('deposits')}>Депозиты</button>
          <button className={`btn ${view === 'promo' ? 'accent' : ''}`} onClick={() => setView('promo')}>Акции</button>
        </div>
        <div className="row">
          {(locations.data?.length ?? 0) > 1 && (
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}
              title="Точка">
              <option value="">Все точки</option>
              {locations.data!.filter((l) => l.isActive !== false).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          {view === 'checks' && (
            <button className="btn" onClick={() => {
              void (async () => {
                const d = DayCloseReport.parse(
                  await (await fetch(`${API}/reports/day-close?date=${date}${locQ}`, { headers: h })).json(),
                );
                setDayClose(d);
              })();
            }}>Закрыть день</button>
          )}
          {view === 'checks' && (
            <button className="btn" onClick={() => {
              void (async () => {
                const d = SalesRegister.parse(
                  await (await fetch(`${API}/reports/sales-register?from=${date}&to=${date}${locQ}`, { headers: h })).json(),
                );
                downloadCsv(`реестр_1С_${date}.csv`, [
                  ['Дата', 'Время', '№ чека', 'Тип', 'Фиск. №', 'Номенклатура', 'Кол-во', 'Цена, ₸', 'Сумма, ₸', 'Себестоимость, ₸', 'Оплата'],
                  ...d.rows.map((x) => [
                    new Date(x.at).toLocaleDateString('ru-RU'),
                    new Date(x.at).toLocaleTimeString('ru-RU'),
                    x.number,
                    x.kind === 'REFUND' ? 'Возврат' : 'Продажа',
                    x.fiscalNumber ?? '',
                    x.name, x.qty, tg(x.price), tg(x.sum),
                    x.cost === null ? '' : tg(x.cost),
                    x.methods.join('+'),
                  ]),
                  ['', '', '', '', '', 'ИТОГО', '', '', tg(d.totalSum), tg(d.totalCost), ''],
                ]);
              })();
            }}>Реестр 1С</button>
          )}
          <button className="btn" onClick={() => void (async () => {
            /* Раньше выбор между двумя выгрузками делался окном, где
               «Отмена» означала «выгрузить другое». Теперь это выбор. */
            let wantPairs = false;
            if (view === 'abc' && pairs.data && pairs.data.rows.length > 0) {
              const a = await ask({
                title: 'Что выгрузить',
                sub: 'По ABC-анализу есть два разных файла.',
                choices: {
                  label: 'Файл',
                  initial: 'abc',
                  options: [
                    { id: 'abc', title: 'ABC-анализ', note: 'блюда по доле в выручке, классы A, B, C' },
                    { id: 'pairs', title: 'Часто берут вместе', note: `пар: ${pairs.data.rows.length}` },
                  ],
                },
                confirmLabel: 'Скачать',
              });
              if (!a) return;
              wantPairs = a.choice === 'pairs';
            }
            if (view === 'checks' && checks.data) {
              downloadCsv(`чеки_${date}.csv`, [
                ['№', 'Время', 'Кассир', 'Оплата', 'Сумма, ₸', 'Статус'],
                ...checks.data.rows.map((x) => [
                  x.number,
                  new Date(x.closedAt).toLocaleTimeString('ru-RU'),
                  x.cashierName,
                  x.methods.join('+'),
                  tg(x.total),
                  x.status === 'CANCELLED' ? `возврат${x.cancelReason ? `: ${x.cancelReason}` : ''}${x.cancelApprover ? ` (${x.cancelApprover})` : ''}` : '',
                ]),
                [],
                ['День недели', 'Выручка, ₸'],
                ...(() => {
                  const WD = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
                  const byWd = new Array(7).fill(0) as number[];
                  for (const x of checks.data.rows) {
                    if (x.status === 'CANCELLED') continue;
                    const d = (new Date(x.closedAt).getDay() + 6) % 7;
                    byWd[d] = (byWd[d] ?? 0) + x.total;
                  }
                  return WD.map((w, i) => [w, tg(byWd[i] ?? 0)]);
                })(),
              ]);
            } else if (view === 'abc' && pairs.data && pairs.data.rows.length > 0 && wantPairs) {
              downloadCsv(`пары_${new Date().toISOString().slice(0, 10)}.csv`, [
                ['Блюдо', 'С чем берут', 'Раз вместе'],
                ...pairs.data.rows.map((x) => [x.a, x.b, x.count]),
              ]);
            } else if (view === 'abc' && abc.data) {
              downloadCsv(`abc_${from}_${to}.csv`, [
                ['Блюдо', 'Кол-во', 'Выручка, ₸', 'Доля, %', 'Класс'],
                ...abc.data.rows.map((x) => [x.name, x.qty, tg(x.revenue), +(x.share * 100).toFixed(1), x.grade]),
              ]);
            } else if (view === 'hours' && hours.data) {
              downloadCsv(`часы_${from}_${to}.csv`, [
                ['Час', 'Чеков', 'Выручка, ₸', 'Ср. чек, ₸'],
                ...hours.data.rows.map((x) => [`${x.hour}:00`, x.checks, tg(x.revenue),
                  x.checks ? tg(Math.round(x.revenue / x.checks)) : '']),
                ...(heat.data && heat.data.rows.length > 0
                  ? (() => {
                      const WD = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
                      return [
                        [],
                        ['Дыхание недели (28 дн.)', '', '', ''],
                        ['День', 'Час', 'Выручка, ₸', ''],
                        ...heat.data.rows
                          .slice()
                          .sort((a, b) => a.wd - b.wd || a.hour - b.hour)
                          .map((x) => [WD[x.wd]!, `${x.hour}:00`, tg(x.revenue), '']),
                      ];
                    })()
                  : []),
              ]);
            } else if (view === 'deposits' && deps.data) {
              downloadCsv(`депозиты_${from}_${to}.csv`, [
                ['Бронь', 'Гость', 'Стол', 'Депозит, ₸', 'Статус', 'Использован'],
                ...deps.data.rows.map((x) => [
                  new Date(x.at).toLocaleString('ru-RU'), x.guestName, x.tableName,
                  tg(x.deposit),
                  x.status === 'PLANNED' ? 'ждём' : x.status === 'SEATED' ? 'сел'
                    : x.status === 'CANCELLED' ? 'отменена' : 'не пришёл',
                  x.used ? 'да' : '',
                ]),
              ]);
            } else if (view === 'speed' && speed.data) {
              downloadCsv(`кухня_${from}_${to}.csv`, [
                ['Час', 'Заказов', 'Средне до READY, сек'],
                ...speed.data.rows.map((x) => [`${x.hour}:00`, x.tickets, x.avgSec]),
              ]);
            } else if (view === 'calls' && callsRep.data) {
              downloadCsv(`вызовы_${from}_${to}.csv`, [
                ['Когда', 'Стол', 'Тип', 'Реакция, сек'],
                ...callsRep.data.rows.map((x) => [
                  new Date(x.at).toLocaleString('ru-RU'),
                  x.tableName, x.kind === 'BILL' ? 'счёт' : 'официант',
                  x.reactionSec ?? '',
                ]),
              ]);
            } else if (view === 'reviews' && reviews.data) {
              downloadCsv(`отзывы_${from}_${to}.csv`, [
                ['Дата', 'Оценка', 'Чек №', 'Комментарий'],
                ...reviews.data.rows.map((x) => [
                  new Date(x.at).toLocaleString('ru-RU'),
                  x.rating, x.orderNumber ?? '', x.comment ?? '',
                ]),
              ]);
            } else if (view === 'cashops' && cashops.data) {
              downloadCsv(`внесения_${from}_${to}.csv`, [
                ['Дата', 'Время', 'Операция', 'Сумма, ₸', 'Кто', 'Комментарий'],
                ...cashops.data.rows.map((x) => [
                  new Date(x.at).toLocaleDateString('ru-RU'),
                  new Date(x.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
                  x.kind === 'PAY_IN' ? 'Внесение' : 'Изъятие',
                  tg(x.amount), x.byName, x.comment ?? '',
                ]),
              ]);
            } else if (view === 'cons' && cons.data) {
              downloadCsv(`расход_${from}_${to}.csv`, [
                ['Позиция', 'Ед.', 'Расход', 'Себестоимость/ед, ₸', 'На сумму, ₸'],
                ...cons.data.rows.map((x) => [
                  x.name, UNIT_RU2[x.unit] ?? x.unit, x.used,
                  x.avgCost === null ? '' : tg(x.avgCost),
                  x.costValue === null ? '' : tg(x.costValue),
                ]),
              ]);
            } else if (view === 'staff' && staffSales.data) {
              downloadCsv(`сотрудники_${from}_${to}.csv`, [
                ['Сотрудник', 'Набрал столов', 'На сумму, ₸', 'Закрыл чеков', 'На сумму, ₸'],
                ...staffSales.data.rows.map((x) => [
                  x.name, x.openedChecks, tg(x.openedSum),
                  x.openedChecks ? tg(Math.round(x.openedSum / x.openedChecks)) : '',
                  x.closedChecks, tg(x.closedSum),
                ]),
              ]);
            } else if (view === 'foodcost' && foodcost.data) {
              downloadCsv(`фудкост_${from}_${to}.csv`, [
                ['Блюдо', 'Кол-во', 'Выручка, ₸', 'Себестоимость/порц, ₸', 'Себестоимость, ₸', 'Маржа, ₸', 'Фудкост, %'],
                ...foodcost.data.rows.map((x) => [
                  x.name, x.qty, tg(x.revenue),
                  x.unitCost === null ? '' : tg(x.unitCost),
                  x.cost === null ? '' : tg(x.cost),
                  x.margin === null ? '' : tg(x.margin),
                  x.pct === null ? '' : +(x.pct * 100).toFixed(1),
                ]),
              ]);
            } else if (view === 'promo' && promo.data) {
              downloadCsv(`акции_${from}_${to}.csv`, [
                ['Акция', 'Чеков', 'Подарили, ₸'],
                ...promo.data.rows.map((x) => [x.name, x.count, tg(x.amount)]),
              ]);
            }
          })()}>Скачать CSV</button>
          {view === 'checks' && checks.data && (
            <button className="btn" title="Реестр продаж в Excel"
              onClick={() => downloadXls(`продажи_${from}_${to}.xls`, [
                ['№', 'Время', 'Кассир', 'Оплата', 'Сумма, ₸', 'Статус'],
                ...checks.data!.rows.map((x) => [
                  x.number, new Date(x.closedAt).toLocaleString('ru-RU'),
                  x.cashierName, x.methods.join('+'), x.total / 100, x.status === 'CLOSED' ? 'закрыт' : 'отменён',
                ]),
              ])}>Excel</button>
          )}
          {view === 'checks' ? (
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          ) : (
            <>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              <span>—</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </>
          )}
        </div>
      </div>

      {view === 'checks' && (
        <>
          {checks.isPending && <p className="empty">Считаем…</p>}
          {checks.data && (
            <>
              <div className="kpis" style={{ marginBottom: 14 }}>
                <div className="kpi"><span>Выручка</span><b className="money">{formatMoney(checks.data.revenue)}</b></div>
                {(() => {
                  const live = checks.data.rows.filter((x) => x.status !== 'CANCELLED');
                  if (live.length === 0) return null;
                  const cashless = live.filter((x) =>
                    x.methods.some((mm) => mm === 'CARD' || mm === 'KASPI_QR')).length;
                  if (cashless === 0) return null;
                  return (
                    <div className="kpi"><span>Чеков с безналом</span>
                      <b>{cashless} из {live.length} · {Math.round((cashless / live.length) * 100)}%</b></div>
                  );
                })()}
                {(checks.data.tips ?? 0) > 0 && (
                  <div className="kpi"><span>На чай картой</span>
                    <b className="money">{formatMoney(checks.data.tips!)}</b></div>
                )}
                {(() => {
                  const ret = checks.data.rows.filter((x) => x.status === 'CANCELLED');
                  return ret.length > 0 ? (
                    <div className="kpi"><span>Возвраты</span>
                      <b className="money margin-thin">{ret.length} · {formatMoney(ret.reduce((s, x) => s + x.total, 0))}</b></div>
                  ) : null;
                })()}
                {(() => {
                  // профиль недели: закрытые чеки периода по дням пн–вс
                  const WD = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
                  const byWd = new Array(7).fill(0) as number[];
                  const cntWd = new Array(7).fill(0) as number[];
                  for (const x of checks.data.rows) {
                    if (x.status === 'CANCELLED') continue;
                    const d = (new Date(x.closedAt).getDay() + 6) % 7;
                    byWd[d] = (byWd[d] ?? 0) + x.total;
                    cntWd[d] = (cntWd[d] ?? 0) + 1;
                  }
                  const max = Math.max(...byWd, 1);
                  if (byWd.every((v) => v === 0)) return null;
                  // лучший день по среднему чеку — не всегда лучший по выручке
                  const avgWd = byWd.map((v, i) => (cntWd[i] ? Math.round(v / cntWd[i]!) : 0));
                  const bestAvg = avgWd.indexOf(Math.max(...avgWd));
                  return (
                    <div className="spark" title="Выручка по дням недели за период">
                      {byWd.map((v, i) => (
                        <span key={WD[i]} className="spark-bar"
                          title={`${WD[i]}: ${formatMoney(v)}${cntWd[i] ? ` · ${cntWd[i]} чек. · ср. ${formatMoney(avgWd[i]!)}` : ''}`}
                          style={{ height: `${Math.max(10, Math.round((v / max) * 100))}%`, opacity: v === max ? 1 : .6 }} />
                      ))}
                      <span className="hint-inline" style={{ marginLeft: 8 }}>
                        пн–вс{avgWd[bestAvg] ? ` · щедрее всего ${WD[bestAvg]}: ср. ${formatMoney(avgWd[bestAvg]!)}` : ''}
                      </span>
                    </div>
                  );
                })()}
                {(() => {
                  const reasons = new Map<string, number>();
                  for (const x of checks.data.rows) {
                    if (x.cancelReason) reasons.set(x.cancelReason, (reasons.get(x.cancelReason) ?? 0) + 1);
                  }
                  if (reasons.size === 0) return null;
                  return (
                    <p className="hint-inline" style={{ margin: '8px 0 0' }}>
                      Возвраты по причинам: {[...reasons.entries()]
                        .sort((a, b) => b[1] - a[1])
                        .map(([why, n]) => `«${why}» ×${n}`).join(' · ')}
                    </p>
                  );
                })()}
                {aw.data && aw.data.rows.length >= 2 && (() => {
                  const max = Math.max(...aw.data!.rows.map((x) => x.avg), 1);
                  return (
                    <div className="spark" title="Средний чек по неделям (8 недель)">
                      {aw.data!.rows.map((x) => (
                        <span key={x.weekStart} className="spark-bar"
                          title={`${x.weekStart}: ср. ${formatMoney(x.avg)} · ${x.checks} чек.`}
                          style={{ height: `${Math.max(12, Math.round((x.avg / max) * 100))}%` }} />
                      ))}
                      <span className="hint-inline" style={{ marginLeft: 8 }}>
                        ср. чек по неделям
                      </span>
                    </div>
                  );
                })()}
                {fc.data && fc.data.daysLeft > 0 && fc.data.monthSoFar > 0 && (
                  <p className="hint-inline" style={{ margin: '10px 0 2px' }}>
                    Месяц идёт на {formatMoney(fc.data.projected)}: собрано {formatMoney(fc.data.monthSoFar)},
                    средний день {formatMoney(fc.data.avgDay)}, осталось {fc.data.daysLeft} дн.
                    Ориентир, не пророчество.
                  </p>
                )}
                {byLoc.data && byLoc.data.rows.length > 1 && (() => {
                  const max = Math.max(...byLoc.data!.rows.map((x) => x.revenue), 1);
                  return (
                    <div className="byloc">
                      {byLoc.data!.rows.map((x) => (
                        <div key={x.locationId} className="wd-row">
                          <span className="wd-name" style={{ width: 'auto' }}>{x.name}</span>
                          <span className="wd-bar-wrap">
                            <span className="wd-bar" style={{ width: `${Math.round((x.revenue / max) * 100)}%` }} />
                          </span>
                          <span className="money right">{formatMoney(x.revenue)}</span>
                          <span className="hint-inline right">{x.checks} чек.{x.checks ? ` · ср. ${formatMoney(Math.round(x.revenue / x.checks))}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div className="kpi"><span>Чеков</span><b>{checks.data.checks}</b></div>
              </div>
              {checks.data.rows.length === 0 && (
                /* Пусто — не всегда «не работали». Чаще смена ещё идёт
                   и чеки не закрыты: отправляем во вкладку «Заказы»,
                   где видно и незакрытые столы */
                <div className="empty">
                  <p>За этот день закрытых чеков нет.</p>
                  <p className="hint">
                    Если смена идёт, заказы могут быть ещё не оплачены —
                    их видно во вкладке «Заказы» вместе с забытыми столами.
                  </p>
                </div>
              )}
              {checks.data.rows.length > 0 && (
                <div className="rep-table">
                  {checks.data.rows.map((r) => (
                    <div key={r.orderId} className={`rep-row ${r.status === 'CANCELLED' ? 'muted' : ''}`}>
                      <b>№{r.number}</b>
                      <span>{new Date(r.closedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
                      <span>{r.cashierName}</span>
                      <span>{r.methods.map((m) => METHOD_RU[m] ?? m).join('+')}
                        {r.status === 'CANCELLED' && (
                          <em className="ret-tag" title={r.cancelApprover ? `Одобрил: ${r.cancelApprover}` : undefined}>
                            {' '}возврат{r.cancelReason ? ` · ${r.cancelReason}` : ''}
                          </em>
                        )}</span>
                      <span className="money right">{formatMoney(r.total)}</span>
                      {/* Гость вернулся через час, нужен документ для
                          бухгалтерии, спор о сумме — копия решает всё это
                          за секунду вместо диктовки по экрану */}
                      <button className="btn small ghost" onClick={() => setCopyOf(r.orderId)}>
                        Копия
                      </button>
                      {/* Правка закрытого чека: сумму не трогаем никогда,
                          меняется способ оплаты и привязка гостя */}
                      <button className="btn small ghost" onClick={() => setFixOf(r)}>
                        Исправить
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {view === 'abc' && (
        <>
          {abc.isPending && <p className="empty">Считаем…</p>}
          {abc.data && abc.data.rows.length === 0 && <p className="empty">За период продаж не было.</p>}
          {abc.data && abc.data.rows.length > 0 && (
            <div className="rep-table abc">
              <div className="rep-row head">
                <span>Блюдо</span><span className="right">Кол-во</span>
                <span className="right">Выручка</span><span className="right">Доля</span><span />
              </div>
              {abc.data.rows.map((r) => (
                <div key={r.name} className="rep-row">
                  <span>{r.name}</span>
                  <span className="right">{r.qty}</span>
                  <span className="money right">{formatMoney(r.revenue)}</span>
                  <span className="right">{(r.share * 100).toFixed(1)}%</span>
                  <b className={`grade g-${r.grade}`}>{r.grade}</b>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === 'hours' && (
        <>
          {hours.isPending && <p className="empty">Считаем…</p>}
          {hours.data && hours.data.peakHour === null && (
            <p className="empty">За период продаж не было — гистограмме нечего показать.</p>
          )}
          {hours.data && hours.data.peakHour !== null && (
            <>
              <p className="hours-peak">Пик выручки — <b>{hours.data.peakHour}:00</b>
                {(() => {
                  const pk = hours.data!.rows.find((x) => x.hour === hours.data!.peakHour);
                  return pk && pk.checks
                    ? <> · средний чек в пик {formatMoney(Math.round(pk.revenue / pk.checks))}</>
                    : null;
                })()}. Смены и заготовки стоит подгонять под него.</p>
              <div className="hours-chart" role="img" aria-label="Выручка по часам">
                {(() => {
                  const max = Math.max(...hours.data.rows.map((x) => x.revenue), 1);
                  return hours.data.rows.map((x) => (
                    <div key={x.hour} className="hbar-col"
                      title={`${x.hour}:00 — ${formatMoney(x.revenue)} · ${x.checks} чек.${x.checks ? ` · ср. ${formatMoney(Math.round(x.revenue / x.checks))}` : ''}`}>
                      <div className={`hbar ${x.hour === hours.data!.peakHour ? 'peak' : ''}`}
                        style={{ height: `${Math.round((x.revenue / max) * 100)}%` }} />
                      {x.hour % 3 === 0 && <span className="hbar-label">{x.hour}</span>}
                    </div>
                  ));
                })()}
              </div>
                    {heat.data && heat.data.rows.length === 0 && (
                      <p className="empty">Карта недели появится после сотни чеков — иначе клетки случайны.</p>
                    )}
                    {heat.data && heat.data.rows.length > 0 && (() => {
                  const max = Math.max(...heat.data!.rows.map((x) => x.revenue), 1);
                  const by = new Map(heat.data!.rows.map((x) => [`${x.wd}:${x.hour}`, x.revenue]));
                  const WD = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
                  return (
                    <>
                      <h3 style={{ margin: '18px 0 6px' }}>Дыхание недели (28 дней)
                        {' '}<button className="memo-x btn-inline" onClick={() => {
                          const WD = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
                          downloadCsv('теплокарта_28дн.csv', [
                            ['День', 'Час', 'Выручка, ₸'],
                            ...heat.data!.rows.slice()
                              .sort((a, b) => a.wd - b.wd || a.hour - b.hour)
                              .map((x) => [WD[x.wd]!, `${x.hour}:00`, tg(x.revenue)]),
                          ]);
                        }}>CSV</button>
                      </h3>
                      <div className="heat" title="Темнее — больше выручки в этот час этого дня">
                        {WD.map((w, wd) => (
                          <div key={w} className="heat-row">
                            <span className="heat-wd">{w}</span>
                            {Array.from({ length: 24 }, (_, hh) => {
                              const v = by.get(`${wd}:${hh}`) ?? 0;
                              return (
                                <i key={hh} className="heat-cell"
                                  title={`${w} ${hh}:00 — ${formatMoney(v)}`}
                                  style={{ opacity: v === 0 ? 0.08 : 0.15 + 0.85 * (v / max) }} />
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
                    {modes.data && modes.data.rows.length > 0 && (
                <>
                  <h3 style={{ margin: '18px 0 6px' }}>По режимам</h3>
                  <div className="rep-table modes">
                    {modes.data.rows.map((x) => (
                      <div key={x.mode} className="rep-row">
                        <span>{x.mode === 'DINE_IN' ? '🍽 Зал' : x.mode === 'TAKEAWAY' ? '🥡 С собой' : '🛵 Доставка'}</span>
                        <span className="right">{x.checks}</span>
                        <span className="money right">{formatMoney(x.revenue)}</span>
                        <span className="hint-inline right">
                          {x.checks ? `ср. ${formatMoney(Math.round(x.revenue / x.checks))}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <h3 style={{ margin: '18px 0 6px' }}>По дням недели</h3>
                    <div className="wd-grid">
                      {(() => {
                        const names = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
                        const max = Math.max(...hours.data!.byWeekday.map((w) => w.revenue), 1);
                        return hours.data!.byWeekday.map((w) => (
                          <div key={w.dow} className="wd-row">
                            <span className="wd-name">{names[w.dow]}</span>
                            <span className="wd-bar-wrap">
                              <span className="wd-bar" style={{ width: `${Math.round((w.revenue / max) * 100)}%` }} />
                            </span>
                            <span className="money right">{formatMoney(w.revenue)}</span>
                            <span className="hint-inline right">
                              {w.days ? `${formatMoney(w.avgPerDay)}/день` : ''}
                              {w.checks ? ` · ср. чек ${formatMoney(Math.round(w.revenue / w.checks))}` : ''}
                            </span>
                          </div>
                        ));
                      })()}
                    </div>
            </>
          )}
        </>
      )}

      {view === 'cons' && (
        <>
          {cons.isPending && <p className="empty">Считаем…</p>}
          {cons.data && cons.data.rows.length === 0 && (
            <p className="empty">За период расхода по учётным позициям не было.</p>
          )}
          {cons.data && cons.data.rows.length > 0 && (
            <>
              <p className="hours-peak">
                Ушло на сумму: <b className="money">{formatMoney(cons.data.totalValue)}</b>
                <small className="hint-inline"> · по средневзвешенной; тех-карты развёрнуты</small>
              </p>
              <div className="rep-table cons">
                <div className="rep-row head">
                  <span>Позиция</span><span className="right">Расход</span>
                  <span className="right">Себест./ед</span><span className="right">На сумму</span>
                </div>
                {cons.data.rows.map((x) => (
                  <div key={x.name} className="rep-row">
                    <span>{x.name}</span>
                    <span className="right">{x.used} {UNIT_RU2[x.unit] ?? ''}</span>
                    <span className="money right">{x.avgCost === null ? '—' : formatMoney(x.avgCost)}</span>
                    <span className="money right">{x.costValue === null ? '—' : formatMoney(x.costValue)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {view === 'deposits' && (
        <>
          {deps.isPending && <p className="empty">Считаем…</p>}
          {deps.data && deps.data.rows.length === 0 && (
            <p className="empty">Депозитов в периоде нет — брони без предоплат.</p>
          )}
          {deps.data && deps.data.rows.length > 0 && (
            <>
              <p className="hours-peak">
                Внесено <b>{formatMoney(deps.data.total)}</b>
                <span className="hint-inline"> · использовано {formatMoney(deps.data.used)}
                  {deps.data.burned > 0 ? ` · сгорело ${formatMoney(deps.data.burned)}` : ''}</span>
              </p>
              <div className="rep-table deposits">
                {deps.data.rows.map((x, i) => (
                  <div key={i} className="rep-row">
                    <span>{new Date(x.at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    <span>{x.guestName} · {x.tableName}</span>
                    <span className="money right">{formatMoney(x.deposit)}</span>
                    <span className={x.used ? '' : x.status === 'NO_SHOW' || x.status === 'CANCELLED' ? 'margin-thin' : 'hint-inline'}>
                      {x.used ? '✓ в чеке' : x.status === 'PLANNED' ? 'ждём' : x.status === 'SEATED' ? 'сел' : x.status === 'CANCELLED' ? 'сгорел · отмена' : 'сгорел · не пришёл'}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {view === 'speed' && (
        <>
          {speed.isPending && <p className="empty">Считаем…</p>}
          {speed.data && speed.data.tickets === 0 && (
            <p className="empty">Пока нечего мерить: метка READY копится с этого обновления.</p>
          )}
          {speed.data && speed.data.tickets > 0 && (
            <>
              <p className="hours-peak">
                Средне до READY: <b>{(() => { const v = speed.data!.avgSec ?? 0; return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`; })()}</b>
                <span className="hint-inline"> · {speed.data.tickets} заказов</span>
              </p>
              <div className="rep-table speed">
                <div className="rep-row head"><span>Час</span><span className="right">Заказов</span><span className="right">До READY</span></div>
                {speed.data.rows.map((x) => (
                  <div key={x.hour} className="rep-row">
                    <span>{x.hour}:00</span>
                    <span className="right">{x.tickets}</span>
                    <span className={`right ${x.avgSec > 900 ? 'margin-thin' : ''}`}>
                      {(() => { const v = x.avgSec; return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`; })()}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {view === 'calls' && (
        <>
          {callsRep.isPending && <p className="empty">Считаем…</p>}
          {callsRep.data && callsRep.data.count === 0 && (
            <p className="empty">Вызовов не было — либо тишина, либо QR столов ещё не расклеены.</p>
          )}
          {callsRep.data && callsRep.data.count > 0 && (
            <>
              <p className="hours-peak">
                Средняя реакция: <b>{callsRep.data.avgSec === null ? '—' : `${Math.floor(callsRep.data.avgSec / 60)} мин ${callsRep.data.avgSec % 60} с`}</b>
                <span className="hint-inline"> · {callsRep.data.count} вызовов</span>
              </p>
              <div className="rep-table calls">
                {callsRep.data.rows.map((x, i) => (
                  <div key={i} className="rep-row">
                    <span>{new Date(x.at).toLocaleString('ru-RU', {
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                    })}</span>
                    <span>{x.tableName}</span>
                    <span>{x.kind === 'BILL' ? '🧾 счёт' : '🙋 официант'}</span>
                    <span className={x.reactionSec === null ? 'hint-inline'
                      : x.reactionSec > 300 ? 'margin-thin' : ''}>
                      {x.reactionSec === null ? 'без ответа'
                        : `${Math.floor(x.reactionSec / 60)}:${String(x.reactionSec % 60).padStart(2, '0')}`}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {view === 'reviews' && (
        <>
          {reviews.isPending && <p className="empty">Считаем…</p>}
          {reviews.data && reviews.data.count === 0 && (
            <p className="empty">За период оценок не было — QR на чеке ждёт первых гостей.</p>
          )}
          {reviews.data && reviews.data.count > 0 && (
            <>
              <p className="hours-peak">
                ★{reviews.data.avg?.toFixed(1)} <span className="hint-inline">· {reviews.data.count} отзывов</span>
              </p>
              <div className="row" style={{ gap: 6, marginBottom: 8 }}>
                {[0, 5, 4, 3, 2, 1].map((n) => (
                  <button key={n} className={`btn small ${ratingFilter === n ? 'accent' : ''}`}
                    onClick={() => setRatingFilter(n)}>{n === 0 ? 'Все' : `${n}★`}</button>
                ))}
              </div>
              <div className="rep-table reviews">
                {reviews.data.rows
                  .filter((x) => ratingFilter === 0 || x.rating === ratingFilter)
                  .map((x, i) => (
                    <div key={i} className="rep-row">
                      <span>{new Date(x.at).toLocaleString('ru-RU', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}</span>
                      <span>{'★'.repeat(x.rating)}</span>
                      <span className="hint-inline">{x.orderNumber ? `чек №${x.orderNumber}` : ''}</span>
                      <span>{x.comment ?? <span className="hint-inline">—</span>}</span>
                    </div>
                  ))}
              </div>
            </>
          )}
        </>
      )}

      {view === 'cashops' && (
        <>
          {cashops.isPending && <p className="empty">Считаем…</p>}
          {cashops.data && cashops.data.rows.length === 0 && (
            <p className="empty">За период ящик мимо чека не открывали.</p>
          )}
          {cashops.data && cashops.data.rows.length > 0 && (
            <>
              <p className="hours-peak">
                Внесения: <b className="money">{formatMoney(cashops.data.totalIn)}</b>
                {' · '}Изъятия: <b className="money">{formatMoney(cashops.data.totalOut)}</b>
              </p>
              <div className="rep-table cashops">
                <div className="rep-row head">
                  <span>Когда</span><span>Операция</span>
                  <span className="right">Сумма</span><span>Кто · комментарий</span>
                </div>
                {cashops.data.rows.map((x, i) => (
                  <div key={i} className="rep-row">
                    <span>{new Date(x.at).toLocaleString('ru-RU', {
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                    })}</span>
                    <span>{x.kind === 'PAY_IN' ? '+ Внесение' : '− Изъятие'}</span>
                    <span className="money right">{formatMoney(x.amount)}</span>
                    <span className="hint-inline">{x.byName}{x.comment ? ` · ${x.comment}` : ''}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {view === 'staff' && (
        <>
          {staffSales.isPending && <p className="empty">Считаем…</p>}
          {staffSales.data && staffSales.data.rows.length === 0 && (
            <p className="empty">За период продаж не было.</p>
          )}
          {staffSales.data && staffSales.data.rows.length > 0 && (
            <div className="rep-table staff-sales">
              <div className="rep-row head">
                <span>Сотрудник</span>
                <span className="right">Набрал столов</span><span className="right">На сумму</span>
                <span className="right">Ср. чек</span>
                <span className="right">Закрыл чеков</span><span className="right">На сумму</span>
              </div>
              {staffSales.data.rows.map((x) => (
                <div key={x.name} className="rep-row">
                  <span>{x.name}</span>
                  <span className="right">{x.openedChecks}</span>
                  <span className="money right">{formatMoney(x.openedSum)}</span>
                  <span className="hint-inline right">
                    {x.openedChecks ? `ср. ${formatMoney(Math.round(x.openedSum / x.openedChecks))}` : '—'}
                  </span>
                  <span className="right">{x.closedChecks}</span>
                  <span className="money right">{formatMoney(x.closedSum)}</span>
                </div>
              ))}
            </div>
          )}
          <p className="hint-inline" style={{ display: 'block', marginTop: 8 }}>
            «Набрал» — кто открыл стол (официант), «закрыл» — кто принял оплату.
            Обычный заказ кассира считается в обеих колонках.
          </p>
        </>
      )}

      {view === 'foodcost' && (
        <>
          {foodcost.isPending && <p className="empty">Считаем…</p>}
          {foodcost.data && foodcost.data.rows.length === 0 && (
            <p className="empty">За период продаж не было.</p>
          )}
          {foodcost.data && foodcost.data.rows.length > 0 && (
            <>
              <p className="hours-peak">
                Маржа: <b className="money">{formatMoney(foodcost.data.margin)}</b>
                {foodcost.data.knownRevenue > 0 && (
                  <> · фудкост {((foodcost.data.cost / foodcost.data.knownRevenue) * 100).toFixed(1)}%</>
                )}
                {foodcost.data.knownRevenue < foodcost.data.revenue && (
                  <> · известна себестоимость {(foodcost.data.knownRevenue / foodcost.data.revenue * 100).toFixed(0)}% выручки —
                  остальному задайте тех-карту или приход</>
                )}
                <small className="hint-inline"> · цены — на момент продажи (снапшот в чеке)</small>
              </p>
              <div className="rep-table fc">
                <div className="rep-row head">
                  <span>Блюдо</span><span className="right">Кол-во</span>
                  <span className="right">Выручка</span><span className="right">Себест./порц</span>
                  <span className="right">Маржа</span><span />
                </div>
                {foodcost.data.rows.map((x) => (
                  <div key={x.productId} className="rep-row">
                    <span>{x.name}</span>
                    <span className="right">{x.qty}</span>
                    <span className="money right">{formatMoney(x.revenue)}</span>
                    <span className="money right">{x.unitCost === null ? '—' : formatMoney(x.unitCost)}</span>
                    <span className="money right">{x.margin === null ? '—' : formatMoney(x.margin)}</span>
                    {x.pct === null
                      ? <span className="grade g-C" title="Нет тех-карты или прихода">?</span>
                      : <b className={`grade ${x.pct < 0.3 ? 'g-A' : x.pct < 0.4 ? 'g-B' : 'fc-hot'}`}>
                          {(x.pct * 100).toFixed(0)}%
                        </b>}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {view === 'abc' && pairs.data && pairs.data.rows.length > 0 && (
        <>
          <h3 style={{ margin: '18px 0 6px' }}>Часто берут вместе (28 дней)</h3>
          <div className="rep-table">
            {pairs.data.rows.map((x) => (
              <div key={`${x.a}·${x.b}`} className="rep-row">
                <span>{x.a} + {x.b}</span>
                <b>×{x.count}</b>
              </div>
            ))}
          </div>
          <p className="hint-inline">Готовые комбо и подсказки официантам — пары уже выбраны гостями.</p>
        </>
      )}
      {view === 'payroll' && (
        payroll.isPending ? <p className="hint">Считаем…</p> :
        !payroll.data || payroll.data.rows.length === 0 ? (
          <p className="note">
            За выбранные дни никто не работал и не продавал.
          </p>
        ) : (
          <>
            <div className="cards">
              <div className="card">
                <span>К выплате всего</span>
                <b>{formatMoney(payroll.data.totals?.total ?? 0)}</b>
              </div>
              <div className="card">
                <span>За часы</span>
                <b>{formatMoney(payroll.data.totals?.byHours ?? 0)}</b>
              </div>
              <div className="card">
                <span>Процент от продаж</span>
                <b>{formatMoney(payroll.data.totals?.byPercent ?? 0)}</b>
              </div>
              {(payroll.data.totals?.byService ?? 0) > 0 && (
                <div className="card">
                  <span>Обслуживание залу</span>
                  <b>{formatMoney(payroll.data.totals!.byService)}</b>
                </div>
              )}
            </div>

            <table className="grid" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th className="num">Часы</th>
                  <th className="num">Чеков</th>
                  <th className="num">Продал</th>
                  <th className="num">Средний</th>
                  <th className="num">За часы</th>
                  <th className="num">Процент</th>
                  <th className="num">Итого</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payroll.data.rows.map((r) => (
                  <tr key={r.id}>
                    <td><b>{r.name}</b></td>
                    <td className="num">{r.hours}</td>
                    <td className="num">{r.checks}</td>
                    <td className="num">{formatMoney(r.sales)}</td>
                    {/* Средний чек рядом с продажами: видно, кто продаёт,
                        а кто разносит одинаковое число тарелок */}
                    <td className="num">{formatMoney(r.avgCheck)}</td>
                    <td className="num">
                      {formatMoney(r.byHours)}
                      <div className="sub">{formatMoney(r.hourlyRate)}/ч</div>
                    </td>
                    <td className="num">
                      {formatMoney(r.byPercent)}
                      <div className="sub">{pctLabel(r.salesPctBp)}</div>
                    </td>
                    <td className="num"><b>{formatMoney(r.total)}</b></td>
                    <td>
                      <button className="btn small ghost" onClick={() => setRatesFor(r)}>
                        Ставки
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="hint" style={{ marginTop: 12 }}>
              Продажи считаются по чекам официанта, а не по кассиру:
              кассир пробивает чужие столы, и записывать их на него
              значит обидеть тех, кто эти столы вёл.
            </p>
          </>
        )
      )}

      {view === 'open' && (
        openBills.isPending ? <p className="hint">Загружаем…</p> :
        !openBills.data || openBills.data.rows.length === 0 ? (
          <p className="note">Открытых счетов нет — весь зал закрыт.</p>
        ) : (
          <>
            <div className="cards">
              <div className="card">
                <span>Открыто столов</span>
                <b>{openBills.data.rows.length}</b>
              </div>
              <div className="card warn">
                <span>Ждут оплаты</span>
                <b>{openBills.data.rows.filter((r) => r.awaitingPay).length}</b>
              </div>
              <div className="card">
                <span>Сумма в зале</span>
                <b>{formatMoney(openBills.data.rows.reduce((s2, r) =>
                  s2 + r.items.reduce((a2, i) => a2 + Math.round(i.qty * i.unitPrice), 0) - r.discount, 0))}</b>
              </div>
            </div>

            <table className="grid" style={{ marginTop: 16 }}>
              <thead>
                <tr><th>Стол</th><th>Официант</th><th>Открыт</th><th>Состояние</th><th className="num">Сумма</th></tr>
              </thead>
              <tbody>
                {openBills.data.rows.map((r) => (
                  <tr key={r.orderId} className={r.awaitingPay ? '' : 'muted'}>
                    <td><b>{r.tableName}</b></td>
                    <td>{r.openedByName}</td>
                    <td>{new Date(r.openedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</td>
                    <td>
                      {r.awaitingPay
                        ? <span className="badge st-expired">ждёт оплаты</span>
                        : <span className="badge st-active">в работе</span>}
                    </td>
                    <td className="num">{formatMoney(
                      r.items.reduce((a2, i) => a2 + Math.round(i.qty * i.unitPrice), 0) - r.discount,
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="hint" style={{ marginTop: 12 }}>
              Эти суммы не входят в выручку: деньги ещё не получены.
              Они появятся в продажах, когда кассир закроет чек.
            </p>
          </>
        )
      )}

      {view === 'voids' && (
        voids.isPending ? <p className="hint">Загружаем…</p> :
        !voids.data || voids.data.total === 0 ? (
          <p className="note">
            За выбранные дни блюда не снимали. Это хороший знак.
          </p>
        ) : (
          <>
            <div className="cards">
              <div className="card">
                <span>Снято позиций</span>
                <b>{voids.data.total}</b>
              </div>
              {voids.data.byReason.slice(0, 3).map((r) => (
                <div key={r.label} className="card">
                  <span>{r.label}</span>
                  <b>{r.count}</b>
                </div>
              ))}
            </div>

            {/* Кто снимает: если один и тот же — разговор с ним,
                если все понемногу — с кухней или с меню */}
            {voids.data.byUser.length > 1 && (
              <div className="rep-row head" style={{ marginTop: 16 }}>
                {voids.data.byUser.map((u) => (
                  <span key={u.name}>{u.name} · {u.count}</span>
                ))}
              </div>
            )}

            <table className="grid" style={{ marginTop: 16 }}>
              <thead>
                <tr><th>Когда</th><th>Блюдо</th><th>Причина</th><th>Кто</th></tr>
              </thead>
              <tbody>
                {voids.data.rows.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.at).toLocaleString('ru-RU', {
                      day: '2-digit', month: '2-digit',
                      hour: '2-digit', minute: '2-digit',
                    })}</td>
                    <td>{r.name}</td>
                    <td>
                      {r.reasonLabel}
                      {r.comment && <div className="sub">{r.comment}</div>}
                    </td>
                    <td>{r.byName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )
      )}

      {view === 'abc' && abc.data && abc.data.rows.length === 0 && (
        <p className="empty">За период продаж не было — отчёт появится после первых чеков.</p>
      )}
      {view === 'abc' && abc.data && abc.data.rows.length === 0 && (
        <p className="empty">За период продаж не было — отчёт появится после первых чеков.</p>
      )}
      {view === 'abc' && abc.data && abc.data.engineering && (
        <div className="eng-grid">
          {([
            ['⭐ Звёзды', 'берегите: популярно и маржинально', abc.data.engineering.stars],
            ['🐎 Лошадки', 'популярны, маржа ниже средней — поднять цену или снизить себестоимость', abc.data.engineering.horses],
            ['🧩 Загадки', 'маржинальны, но редки — продвигать', abc.data.engineering.puzzles],
            ['🐶 Собаки', 'ни частоты, ни маржи — кандидаты на вылет', abc.data.engineering.dogs],
          ] as const).map(([title, hint, rows]) => (
            <div key={title} className="eng-cell">
              <h4>{title}</h4>
              <p className="hint-inline">{hint}</p>
              {rows.length === 0 && <p className="empty small">—</p>}
              {rows.slice(0, 6).map((x) => (
                <div key={x.name} className="eng-row">
                  <span>{x.name}</span>
                  <span className="hint-inline">{x.qty} шт · {formatMoney(x.marginPerUnit)}/шт</span>
                </div>
              ))}
            </div>
          ))}
          {abc.data.engineering.noCost > 0 && (
            <p className="hint-inline" style={{ gridColumn: '1 / -1' }}>
              Вне анализа: {abc.data.engineering.noCost} позиций без себестоимости — заполните тех-карты.
            </p>
          )}
        </div>
      )}

      {view === 'promo' && (
        <>
          {promo.isPending && <p className="empty">Считаем…</p>}
          {promo.data && promo.data.rows.length === 0 && (
            <p className="empty">За период акции не срабатывали.</p>
          )}
          {promo.data && promo.data.rows.length > 0 && (
            <div className="rep-table">
              <div className="rep-row head">
                <span>Акция</span><span className="right">Чеков</span><span className="money right">Подарили гостям</span>
              </div>
              {promo.data.rows.map((r) => (
                <div key={r.name} className="rep-row three">
                  <span>{r.name}</span>
                  <span className="right">{r.count}</span>
                  <span className="money right">{formatMoney(r.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {dayClose && (
        <div className="print-sheet">
          <h1>Закрытие дня — {new Date(dayClose.date).toLocaleDateString('ru-RU')}</h1>
          <p className="ps-sub">{dayClose.place}{dayClose.locationName ? ` · ${dayClose.locationName}` : ' · все точки'}</p>

          <table className="ps-table">
            <tbody>
              <tr><td>Выручка</td><td className="r">{tg(dayClose.revenue)} ₸</td></tr>
              <tr><td>Чеков</td><td className="r">{dayClose.checks}</td></tr>
              <tr><td>Средний чек</td><td className="r">{tg(dayClose.avgCheck)} ₸</td></tr>
              <tr><td>Возвраты</td><td className="r">{dayClose.cancelledCount} шт · {tg(dayClose.cancelledSum)} ₸</td></tr>
              {dayClose.byMethod.map((m) => (
                <tr key={m.method}>
                  <td>{m.method === 'CASH' ? 'Наличные' : m.method === 'CARD' ? 'Карта' : 'Kaspi QR'}</td>
                  <td className="r">{tg(m.amount)} ₸</td>
                </tr>
              ))}
            </tbody>
          </table>

          {dayClose.top.length > 0 && (
            <>
              <h2>Топ позиций</h2>
              <table className="ps-table">
                <tbody>
                  {dayClose.top.map((t) => (
                    <tr key={t.name}><td>{t.name} × {t.qty}</td><td className="r">{tg(t.sum)} ₸</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <h2>Смены</h2>
          <table className="ps-table">
            <thead>
              <tr><th>Кассир</th><th>Время</th><th className="r">Чеков</th>
                <th className="r">Выручка</th><th className="r">Внесения</th>
                <th className="r">Изъятия</th><th className="r">В ящике должно быть</th></tr>
            </thead>
            <tbody>
              {dayClose.shifts.map((s, i) => (
                <tr key={i}>
                  <td>{s.cashier}</td>
                  <td>
                    {new Date(s.openedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    –{s.closedAt
                      ? new Date(s.closedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                      : 'открыта'}
                  </td>
                  <td className="r">{s.checks}</td>
                  <td className="r">{tg(s.revenue)} ₸</td>
                  <td className="r">{s.payIn ? `+${tg(s.payIn)}` : '—'}</td>
                  <td className="r">{s.payOut ? `−${tg(s.payOut)}` : '—'}</td>
                  <td className="r">{tg(s.expectedCash)} ₸</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="ps-total-words">
            Итого выручка прописью: {tengeInWords(dayClose.revenue)}
          </p>
          <p className="ps-sign">Владелец: _______________________</p>
        </div>
      )}

      {copyOf && (
        <ReceiptCopy token={token} orderId={copyOf} onClose={() => setCopyOf(null)} />
      )}
      {ratesFor && (
        <RatesDialog token={token} staff={ratesFor}
          onClose={() => setRatesFor(null)}
          onSaved={() => { setRatesFor(null); void payroll.refetch(); }} />
      )}
      {fixOf && (
        <FixOrder token={token} order={fixOf}
          onClose={() => setFixOf(null)}
          onDone={() => window.location.reload()} />
      )}
      {sheet}
      {noteView}
    </div>
  );
}

/**
 * Копия чека. Собирается тем же кодом, что и оригинал на кассе —
 * расхождения между лентой и копией быть не может по построению.
 */
function ReceiptCopy({ token, orderId, onClose }: {
  token: string; orderId: string; onClose: () => void;
}) {
  const { data, isPending } = useQuery({
    queryKey: ['receipt-copy', orderId],
    queryFn: async () => {
      const r = await fetch(`/api/v1/reports/receipt-copy?orderId=${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('not found');
      return r.json() as Promise<{ lines: string[]; number: number; total: number }>;
    },
  });

  return (
    <div className="dialog">
      <div className="dialog-card">
        <h2>Копия чека{data ? ` №${data.number}` : ''}</h2>
        {isPending ? <p className="hint">Собираем…</p> : (
          <pre className="receipt-copy">{data?.lines.join('\n')}</pre>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Закрыть</button>
          <button className="btn" disabled={!data} onClick={() => {
            void navigator.clipboard?.writeText(data!.lines.join('\n'));
          }}>Скопировать</button>
          <button className="btn primary" disabled={!data}
            onClick={() => window.print()}>Печать</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Исправление закрытого чека.
 *
 * Чек уже в отчёте и в фискальной очереди — сумму не меняем никогда,
 * это подлог. Две вещи путают постоянно, и обе безопасны: способ
 * оплаты (карту пробили как наличные, вечером ящик не сходится)
 * и привязка гостя (забыли карту — визит и бонусы потеряны).
 */
function FixOrder({ token, order, onClose, onDone }: {
  token: string;
  order: { orderId: string; number: number };
  onClose: () => void;
  onDone: () => void;
}) {
  const [method, setMethod] = useState<'CASH' | 'CARD' | null>(null);
  const [reason, setReason] = useState('');
  const [phone, setPhone] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const REASON_RU: Record<string, string> = {
    NOT_CLOSED: 'Чек ещё не закрыт — правьте его на кассе',
    SPLIT_PAYMENT: 'Гость платил двумя способами. Такой чек не правим: он собран осознанно',
    SAME_METHOD: 'Этот способ уже стоит',
    REASON_REQUIRED: 'Опишите причину словами',
    GUEST_NOT_FOUND: 'Гостя с таким телефоном нет в базе',
  };

  const call = async (path: string, body: unknown) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/reports/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => null) as { code?: string } | null;
        setErr(REASON_RU[d?.code ?? ''] ?? 'Не получилось исправить');
        return;
      }
      onDone();
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="dialog" onClick={onClose}>
      <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
        <h2>Исправить чек №{order.number}</h2>
        <p className="hint">
          Сумму чека изменить нельзя — он уже в отчёте и в фискальной
          очереди. Исправить можно то, что путают при закрытии.
        </p>

        <h3 className="fix-part">Способ оплаты</h3>
        <p className="hint">
          Пробили наличными, а гость заплатил картой — вечером ящик
          не сойдётся на эту сумму.
        </p>
        <div className="fix-methods">
          {(['CASH', 'CARD'] as const).map((m) => (
            <button key={m} className={`btn ${method === m ? 'primary' : ''}`}
              onClick={() => setMethod(m)}>
              {m === 'CASH' ? 'Наличные' : 'Карта'}
            </button>
          ))}
        </div>
        {method && (
          <>
            <label>Причина
              <input value={reason} autoFocus placeholder="гость платил картой"
                onChange={(e) => setReason(e.target.value)} />
              <i className="split">Останется в журнале вместе с прежним значением</i>
            </label>
            <button className="btn primary" disabled={busy || reason.trim().length < 3}
              onClick={() => void call('fix-payment', {
                orderId: order.orderId, method, reason: reason.trim(),
              })}>
              Сменить способ оплаты
            </button>
          </>
        )}

        <h3 className="fix-part">Гость</h3>
        <p className="hint">
          Забыли приложить карту — визит не попал в историю, бонусы
          не начислились.
        </p>
        <label>Телефон гостя
          <input value={phone} placeholder="+7 701 …" inputMode="tel"
            onChange={(e) => setPhone(e.target.value)} />
        </label>
        <button className="btn" disabled={busy || phone.trim().length < 6}
          onClick={() => void call('fix-guest', {
            orderId: order.orderId, guestPhone: phone.trim(),
          })}>
          Привязать гостя
        </button>

        {err && <p className="rb-warn">{err}</p>}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Ставки сотрудника.
 *
 * Два числа, и они отвечают на разные вопросы. Ставка за час —
 * за присутствие: человек стоял в зале и носил блюда, даже если
 * гостей было мало. Процент — за старание: предложил десерт,
 * довёл компанию до второго графина.
 *
 * Сбор за обслуживание отдельной галочкой: в части кафе он целиком
 * уходит залу, в части остаётся заведению. Это решение владельца.
 */
function RatesDialog({ token, staff, onClose, onSaved }: {
  token: string;
  staff: { id: string; name: string; hourlyRate: number; salesPctBp: number; getsService: boolean };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rate, setRate] = useState(String(Math.round(staff.hourlyRate / 100)));
  const [pct, setPct] = useState(pctLabel(staff.salesPctBp).replace('%', ''));
  const [service, setService] = useState(staff.getsService);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const bp = pctToBp(pct);
    if (bp === null) { setErr('Процент не понят: пишите 2,5 или 3'); return; }
    const hourly = Math.round(Number(rate.replace(',', '.')) * 100);
    if (!Number.isFinite(hourly) || hourly < 0) { setErr('Ставка не понята'); return; }

    setBusy(true);
    try {
      await fetch(`${API}/reports/payroll/rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId: staff.id, hourlyRate: hourly, salesPctBp: bp, getsService: service }),
      });
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="dialog" onClick={onClose}>
      <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
        <h2>Ставки · {staff.name}</h2>

        <label>Ставка за час, ₸
          <input inputMode="numeric" value={rate}
            onChange={(e) => setRate(e.target.value)} />
          <i className="split">
            За присутствие: человек стоял в зале, даже если гостей было мало.
            Ноль — работает только на проценте
          </i>
        </label>

        <label>Процент от личных продаж
          <input inputMode="decimal" value={pct} placeholder="2,5"
            onChange={(e) => setPct(e.target.value)} />
          <i className="split">
            Считается от его чеков, а не от общей выручки: иначе один
            тянет, другой получает
          </i>
        </label>

        <label className="check">
          <input type="checkbox" checked={service}
            onChange={(e) => setService(e.target.checked)} />
          Сбор за обслуживание достаётся ему
        </label>

        {err && <p className="rb-warn">{err}</p>}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Отмена</button>
          <button className="btn primary" disabled={busy} onClick={() => void save()}>
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}
